import { getFullName } from '@zorro/common-utils';
import { Title } from '@zorro/design-system/v2/Title';
import { parseDateISO } from '@zorro/shared/formatters';
import { Group, Stack, Text } from '@zorro/web/design-system';
import { useAppRouter, useMonolithQuery } from '@zorro/web/ui-utils';

import {
  CommentsButton,
  CommentsDrawer,
  useEnrollmentComments,
} from '../../Comments';
import { EnrollmentStatusBadge } from '../../EnrollmentStatus';
import {
  EmployeePageActionsMenu,
  EnrollmentAction,
} from './EmployeePageActionsMenu';
import { getEnrollmentSubtitle } from './EmployeePageHeaderUtils';
import { ReportLifeEventButton } from './ReportLifeEventButton';

type Props = {
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  isDon: boolean;
};

export const EmployeePageHeader = ({
  onEnrollmentTabActionChange,
  onboardingPeriodId,
  employerId,
  employeeId,
  isDon,
}: Props) => {
  const { navigateToRoute } = useAppRouter();

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });
  const { data: comments = [] } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindManyEnrollmentComments',
    params: [onboardingPeriodId],
    enabled: !isDon,
  });
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  const { showComments, setShowComments, selectedCommentId } =
    useEnrollmentComments();

  if (!onboardingPeriod || !employee) {
    return null;
  }

  const handleReportLifeEventSuccess = (nextOnboardingPeriodId?: string) => {
    if (nextOnboardingPeriodId) {
      navigateToRoute(
        isDon ? 'DON_EMPLOYEE_PLANS_TAB' : 'OPS_EMPLOYEE_PLANS_TAB',
        [
          {
            employerId,
            employeeId,
            onboardingPeriodId: nextOnboardingPeriodId,
          },
        ]
      );
    } else {
      navigateToRoute(isDon ? 'DON_EMPLOYEE_PAGE' : 'OPS_EMPLOYEE_PAGE', [
        { employerId, employeeId },
      ]);
    }
  };

  const year = parseDateISO(onboardingPeriod.enrollmentEffectiveFrom).year();
  const title = onboardingPeriod.isSpecialEnrollment
    ? `Special enrollment ${year}`
    : `Open enrollment ${year}`;

  const employeeName = getFullName(employee);
  const subtitle = getEnrollmentSubtitle(
    onboardingPeriod.enrollmentStatus,
    onboardingPeriod,
    employeeName,
    isDon,
    onboardingPeriod.majorMedicalBenefit?.submittedAt
  );

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-start">
        <Group gap="sm">
          <Title size="h1">{title}</Title>
          <EnrollmentStatusBadge
            enrollmentStatus={onboardingPeriod.enrollmentStatus}
          />
        </Group>
        <Group gap="xs">
          {isDon ? (
            <ReportLifeEventButton
              employeeId={employeeId}
              onboardingPeriodId={onboardingPeriodId}
              onSuccess={handleReportLifeEventSuccess}
            />
          ) : (
            <CommentsButton
              commentsCount={comments.length}
              onClick={() => setShowComments(true)}
            />
          )}
          <EmployeePageActionsMenu
            onEnrollmentTabActionChange={onEnrollmentTabActionChange}
            onboardingPeriodId={onboardingPeriodId}
            employeeId={employeeId}
            employerId={employerId}
          />
        </Group>
      </Group>
      {subtitle && <Text>{subtitle}</Text>}
      <CommentsDrawer
        shouldShowComments={showComments}
        onboardingPeriodId={onboardingPeriodId}
        isSpecialEnrollment={onboardingPeriod.isSpecialEnrollment}
        enrollmentStartDate={onboardingPeriod.enrollmentEffectiveFrom}
        employeeId={employeeId}
        fullName={getFullName(employee)}
        employerId={employerId}
        commentsCount={comments.length}
        selectedCommentId={selectedCommentId}
        setShowComments={setShowComments}
      />
    </Stack>
  );
};
